/**
 * Spanish Locale Formatters
 * All monetary amounts and dates must follow Spanish locale format
 * Money: 82.650,00 € (thousands separator + two decimals + trailing symbol)
 * Dates: DD/MM/YYYY (13/04/2026)
 */

/**
 * Format number as currency in Spanish locale
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted currency string (e.g., "82.650,00 €")
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') {
    return '0,00 €';
  }
  
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '0,00 €';
  }
  
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
};

/**
 * Format date in Spanish locale (DD/MM/YYYY)
 * @param {Date|string|moment} date - The date to format
 * @returns {string} Formatted date string (e.g., "13/04/2026")
 */
export const formatDate = (date) => {
  if (!date) {
    return '';
  }
  
  let dateObj;
  
  // Handle moment objects
  if (date && typeof date === 'object' && date._isAMomentObject) {
    dateObj = date.toDate();
  }
  // Handle string dates
  else if (typeof date === 'string') {
    dateObj = new Date(date);
  }
  // Handle Date objects
  else if (date instanceof Date) {
    dateObj = date;
  }
  else {
    return '';
  }
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Format date with time in Spanish locale (DD/MM/YYYY HH:mm)
 * @param {Date|string|moment} date - The date to format
 * @returns {string} Formatted date string with time (e.g., "13/04/2026 14:30")
 */
export const formatDateTime = (date) => {
  if (!date) {
    return '';
  }
  
  let dateObj;
  
  // Handle moment objects
  if (date && typeof date === 'object' && date._isAMomentObject) {
    dateObj = date.toDate();
  }
  // Handle string dates
  else if (typeof date === 'string') {
    dateObj = new Date(date);
  }
  // Handle Date objects
  else if (date instanceof Date) {
    dateObj = date;
  }
  else {
    return '';
  }
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};
